import { createContainer } from 'unstated-next';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getAdminBasePath } from '../../app/route-utils';
import useAuth from '../../auth/use-auth';
import { useAuthControllerLogin } from '../api/generated/auth/auth';
import type { LocalLoginDto, LoginResponseDto } from '../api/generated/types';
import { useStaticLocales } from '../hooks/use-static-locales';
import type { SystemLocaleCodes } from '../hooks/use-static-locales';

export function getLoginLocale(): SystemLocaleCodes {
  const lang = (navigator.language || '').substring(0, 2).toLowerCase();
  if (lang === 'ko') return 'ko';
  if (lang === 'ja') return 'ja';
  return 'en';
}

function useAdminLoginContainer() {
  const { pathname } = useLocation();
  const adminBasePath = getAdminBasePath(pathname);
  const [, { handleLogin, nav }] = useAuth();
  const { sltStr } = useStaticLocales({ localeCode: getLoginLocale() });

  const [signInForm, setSignInForm] = useState<LocalLoginDto>({
    email: '',
    password: '',
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loginMutation = useAuthControllerLogin({
    mutation: {
      onSuccess: async (response) => {
        const accessToken = (response as LoginResponseDto | undefined)?.access_token;
        if (!accessToken) {
          setErrorMessage(sltStr({ key: 'login-error-general' }));
          return;
        }

        await handleLogin(accessToken);
        setTimeout(() => {
          nav(adminBasePath);
        }, 100);
      },
      onError: (error: any) => {
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          sltStr({ key: 'login-error-general' });
        setErrorMessage(message);
      },
    },
  });

  const handleSubmit = () => {
    if (loginMutation.isPending) return;

    if (!signInForm.email.trim()) {
      setErrorMessage(sltStr({ key: 'login-error-email-required' }));
      return;
    }
    if (!signInForm.password.trim()) {
      setErrorMessage(sltStr({ key: 'login-error-password-required' }));
      return;
    }

    setErrorMessage(null);
    loginMutation.mutate({
      data: {
        email: signInForm.email.trim(),
        password: signInForm.password,
      },
    });
  };

  return {
    signInForm,
    setSignInForm,
    errorMessage,
    setErrorMessage,
    handleSubmit,
    isSubmitting: loginMutation.isPending,
    sltStr,
  };
}

const AdminLoginContainer = createContainer(useAdminLoginContainer);

export default AdminLoginContainer;
